import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FileUploader } from 'react-drag-drop-files';
import { LinearProgress } from '@mui/material';
import Radio from '@mui/material/Radio';
import { useDebouncedCallback } from 'use-debounce';
import { Checkbox } from '@mui/material';
import {
    EmailValidator,
    ErrorText,
    FindErrorItem,
    FindLoad,
    getPhoneErrorText,
    hooksForErrors,
    Images,
    useModal,
    useGlobalStyles,
} from 'utils';
import { AddressInput, CreateChancel, FunderAddress, MaskInput, ToolInfo, ValidationInput } from 'components';
import { httpRequestsOnErrorsActions, systemActions, uploadFile } from 'store';
import { KeyPurpose } from 'utils';
import { taxIdTypes, taxTypeEnums } from '../constants';
import { systemService } from '../../../../store/system/system.service';
import { systemItemStyles } from '../styles';

export const AddCompany = ({ info }) => {
    const ACTION_TYPE = info ? 'EDIT_COMPANY' : 'CREATE_COMPANY';
    const classes = systemItemStyles();
    const globalStyles = useGlobalStyles();
    const [inputs, setInputs] = useState({});
    const [error, setError] = useState('');
    const dispatch = useDispatch();
    const { close } = useModal();
    const loader = FindLoad(ACTION_TYPE);
    const backError = FindErrorItem(ACTION_TYPE);
    const phoneErrorMsg = getPhoneErrorText(inputs.phone);
    const phoneErrorText = hooksForErrors.getPhoneError(error, backError, phoneErrorMsg);
    const emailErrorMsg = !EmailValidator.test(inputs.email) ? ErrorText.emailValid : '';
    const emailErrorText = hooksForErrors.getEmailError(error, backError, emailErrorMsg);
    const [currentFile, setCurrentFile] = useState(null);
    const [uploadLoader, setUploadLoader] = useState(false);
    const [taxType, setTaxType] = useState(taxTypeEnums.SSN);
    const [loadNpiInfo, setLoadNpiInfo] = useState(false);
    const [isDefault, setIsDefault] = useState(false);
    const [enteredAddress, setEnteredAddress] = useState(info?.address ? info.address : '');

    useEffect(() => {
        if (info) {
            setInputs({
                name: info.name,
                npi: info.npi,
                phone: info.phone,
                taxId: info.taxId,
                vendorId: info.vendorId,
                email: info.email,
                taxonomyCode: info?.taxonomyCode,
            });
            if (info.address) setEnteredAddress(info.address);
            if (info.logo) setCurrentFile(info.logo);
            if (info.taxIdType) setTaxType(info.taxIdType);
            if (info.isDefault) setIsDefault(info.isDefault);
        }
    }, [info]);

    useEffect(() => {
        return () => dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
    }, []);

    const handleChange = (e) => {
        setInputs((prevState) => ({ ...prevState, [e.target.name]: e.target.value }));
        error === e.target.name && setError('');
        if (e?.target?.name === 'npi' && e?.target?.value?.length === 10) {
            debounced(e.target.value);
        }
    };

    const handleUpload = async (e) => {
        error === 'currentFile' && setError('');
        if (e) {
            try {
                setUploadLoader(true);
                // Logo goes to the public branding bucket via the
                // generic file upload SDK. Bytes never proxy through
                // api — direct presigned PUT to S3.
                const storedFile = await uploadFile(e, KeyPurpose.LOGO);
                setCurrentFile({ file: storedFile });
                setUploadLoader(false);
            } catch (err) {
                setUploadLoader(false);
            }
        }
    };

    const debounced = useDebouncedCallback(
        (npi) => {
            loadNpiData(npi);
        },
        1000,
        { maxWait: 2000 }
    );

    const loadNpiData = async (npi) => {
        try {
            setLoadNpiInfo(true);
            const res = await systemService.getCompanyByNpi({ number: npi });
            const resData = res?.data;
            if (resData) {
                const params = { ...inputs };
                if (resData?.name) params.name = resData.name;
                if (resData?.taxonomyCode) params.taxonomyCode = resData.taxonomyCode;
                if (resData?.email) params.email = resData.email;
                if (resData?.phone) params.phone = resData.phone.replace(/\D/g, '');
                if (resData?.addressLine1) params.street = resData.addressLine1;
                if (resData?.addressLine2) params.unitNumber = resData.addressLine2;
                if (resData?.city) params.city = resData.city;
                if (resData?.state) params.state = resData.state;
                if (resData?.country) params.country = resData.country;
                if (resData?.zipCode) params.zip = resData.zipCode;
                setInputs(params);
            }
        } catch (err) {
        } finally {
            setLoadNpiInfo(false);
        }
    };

    const handleSubmit = () => {
        const emailIsValid = inputs?.email ? EmailValidator.test(inputs?.email) : false;
        const addressIsValid = enteredAddress?.street && enteredAddress?.city && enteredAddress?.country;

        if (inputs?.npi && inputs?.name && inputs?.taxId && emailIsValid && inputs?.phone && addressIsValid) {
            const params = {
                name: inputs.name,
                npi: inputs.npi,
                taxId: inputs.taxId,
                email: inputs.email,
                vendorId: inputs.vendorId,
                address: enteredAddress,
                taxIdType: taxType,
                isDefault,
                taxonomyCode: inputs?.taxonomyCode || null,
            };
            if (currentFile) {
                params.file = currentFile?.file || currentFile;
            }
            params.phone = parseInt(inputs?.phone.replace(/[^0-9]/g, '')) + '';

            if (info?.id) {
                dispatch(systemActions.editCompany(info.id, params));
            } else {
                dispatch(systemActions.createCompany(params));
            }
        } else {
            setError(
                !inputs?.npi ? 'npi' :
                    !inputs?.name ? 'name' :
                        !inputs?.taxId ? 'taxId' :
                            !inputs?.email ? 'email' :
                                !emailIsValid ? emailErrorMsg :
                                    !inputs?.phone ? 'phone' :
                                        !addressIsValid ? 'address'
                                : ''
            );
        }
    };

    return (
        <div style={{ width: 700 }}>
            <p className="modal-header-title">{info ? 'Edit Company Info' : 'Add Company Info'}</p>
            <div className="modal-body-wrapper">
                <div className="flex-align-center" style={{ gap: 16 }}>
                    <div style={{ width: '50%' }}>
                        <ValidationInput
                            loader={loadNpiInfo}
                            className={globalStyles.inputTextFieldBlue}
                            variant="outlined"
                            name="npi"
                            type="text"
                            label="NPI Number*"
                            typeError={error === 'npi' ? `NPI Number ${ErrorText.isRequired}` : ''}
                            value={inputs?.npi}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="npi-auto-select-info">
                        <ToolInfo
                            text={
                                'Entering an NPI will automatically fill fields like company name, taxonomy code, address, and phone number when available.'
                            }
                        />
                        <p className="npi-info-text">Enter NPI to auto-fill basic company info</p>
                    </div>
                </div>

                <ValidationInput
                    className={globalStyles.inputTextFieldBlue}
                    disabled={loadNpiInfo}
                    variant={'outlined'}
                    onChange={handleChange}
                    value={inputs?.name}
                    type={'text'}
                    label={'Company Name*'}
                    name="name"
                    typeError={error === 'name' ? ErrorText.field : ''}
                />

                <ValidationInput
                    loader={loadNpiInfo}
                    className={globalStyles.inputTextFieldBlue}
                    variant="outlined"
                    name="taxonomyCode"
                    type="text"
                    label="Taxonomy Code"
                    // typeError={error === 'npi' ? `NPI Number ${ErrorText.isRequired}` : ''}
                    value={inputs?.taxonomyCode}
                    onChange={handleChange}
                />

                <div style={{ marginBottom: '20px' }}>
                    <FileUploader types={['png', 'jpeg', 'jpg']} handleChange={handleUpload} name="file" disabled={loadNpiInfo} maxSize={2}>
                        <div style={{ height: '220px' }} className="drag-drop-component">
                            <div className={classes.uploadedImage}>
                                <img
                                    className={classes.currentLogo}
                                    src={
                                        currentFile?.file?.url
                                            ? currentFile?.file?.url
                                            : currentFile?.url
                                            ? currentFile?.url
                                            : Images.uploadImage
                                    }
                                    alt="icon"
                                />
                                {currentFile && (
                                    <button
                                        disabled={loadNpiInfo}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setCurrentFile(null);
                                        }}>
                                        <img src={Images.closeBlackVector} alt={'icon'} />
                                    </button>
                                )}
                            </div>
                            <label className="custom-file-upload">
                                <input onClick={(event) => (event.target.value = null)} onChange={handleUpload} type="file" id="file" />
                                <span className="upload">
                                    <p>
                                        Drag & Drop or <span style={{ color: '#438AFE' }}>Upload</span> Company Logo
                                    </p>
                                    <p style={{ textAlign: 'center', fontSize: '12px', color: '#9E9E9E' }}>
                                        Only jpg, jpeg and png files are supported. Max size 2MB
                                    </p>
                                </span>
                            </label>
                        </div>
                    </FileUploader>
                    {uploadLoader && (
                        <div className="item-loading" style={{ marginTop: '0' }}>
                            <div className="images-info">
                                <img src={Images.document} alt="icon" />
                            </div>
                            <LinearProgress style={{ background: '#347AF0' }} />
                        </div>
                    )}
                </div>

                <div className="flex-align-start" style={{ gap: 16 }}>
                    <div style={{ width: '50%' }}>
                        <ValidationInput
                            className={globalStyles.inputTextFieldBlue}
                            disabled={loadNpiInfo}
                            variant={'outlined'}
                            name={'taxId'}
                            type={'text'}
                            label={'Tax ID*'}
                            typeError={error === 'taxId' ? `Tax ID ${ErrorText.isRequired}` : ''}
                            value={inputs?.taxId}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="flex-align-center" style={{ gap: 16, width: '50%' }}>
                        <p className="tax-id-title">Tax ID Type:*</p>
                        <div className="tax-id-types-wrapper">
                            {taxIdTypes?.map((i, j) => (
                                <Fragment key={j}>
                                    <div className="flex-align-center" style={{ gap: 8 }}>
                                        <Radio
                                            disabled={loadNpiInfo}
                                            onClick={() => setTaxType(i?.value)}
                                            checked={taxType === i?.value}
                                            value={i?.value}
                                            style={{
                                                color: taxType === i?.value ? '#0C66E4' : '#D0D5DD',
                                            }}
                                            disableRipple
                                            classes={{ root: classes.radio, checked: classes.checked }}
                                        />
                                        <p>{i?.label}</p>
                                    </div>
                                </Fragment>
                            ))}
                        </div>
                    </div>
                </div>

                <p className="company-info-title">Contact</p>

                <div className="flex-align-center" style={{ gap: 16 }}>
                    <ValidationInput
                        className={globalStyles.inputTextFieldBlue}
                        disabled={loadNpiInfo}
                        validator={EmailValidator}
                        variant={'outlined'}
                        name={'email'}
                        type={'email'}
                        label={'Email Address*'}
                        typeError={error === 'email' ? `Email Address ${ErrorText.isRequired}` : emailErrorText}
                        value={inputs?.email}
                        onChange={handleChange}
                    />
                    <MaskInput
                        className={globalStyles.inputTextFieldBlue}
                        styles={{ margin: 0 }}
                        disabled={loadNpiInfo}
                        name="phone"
                        label="Phone Number*"
                        value={inputs?.phone}
                        handleChange={handleChange}
                        error={error === 'phone' ? `Phone Number ${ErrorText.isRequired}` : phoneErrorText}
                    />
                </div>

                <div>
                    <p className="company-info-title">Address</p>
                    <FunderAddress
                        placeholder={'Company Address*'}
                        selectedAddress={info?.address}
                        setCurrentAddress={setEnteredAddress}
                        errMessage={error === 'address'}
                        gap={16}
                    />
                </div>

                <ValidationInput
                    className={globalStyles.inputTextFieldBlue}
                    disabled={loadNpiInfo}
                    variant={'outlined'}
                    name={'vendorId'}
                    type={'text'}
                    label={'Vendor ID'}
                    value={inputs?.vendorId}
                    onChange={handleChange}
                />

                <div className="flex-align-center" style={{ gap: 8, margin: '0 0 24px' }}>
                    <Checkbox
                        onClick={() => setIsDefault(!isDefault)}
                        checked={isDefault}
                        style={{
                            padding: 0,
                            color: isDefault ? '#347AF0' : '#D0D5DD',
                        }}
                    />
                    <p className="no-prior-auth">Set as Main Office</p>
                </div>
            </div>

            <CreateChancel
                loader={!!loader.length}
                create={info ? 'Save Changes' : 'Add'}
                chancel={'Cancel'}
                onCreate={handleSubmit}
                onClose={close}
                buttonWidth="100%"
            />
        </div>
    );
};
